import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button } from '@/common/components/Button/Button';
import { ControlledTextField } from '@/common/components/Fields/ControlledTextField/ControlledTextField';
import { Form } from '@/common/components/Form/Form';
import { Icon } from '@/common/components/Icons/Icon';
import { Modal } from '@/common/components/Modal/Modal';
import { Stack } from '@/common/components/Stack/Stack';
import { Translate } from '@/common/components/Translate/Translate';
import { AnalyticsService } from '@/common/integrations/analytics/analytics.service';
import { useRequestPasswordReset } from '@/common/integrations/menhir/employee/sessions/sessions.hooks';
import { notify } from '@/common/utils/toast/toast';
import { zodResolver } from '@hookform/resolvers/zod';
import { IResetPasswordFormData, formSchema } from './ResetPasswordModal.utils';

export function ResetPasswordModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();

  const { mutateAsync: requestPasswordReset, isLoading } =
    useRequestPasswordReset();

  const methods = useForm({
    mode: 'onTouched',
    defaultValues: {
      email: '',
    },
    resolver: zodResolver(formSchema),
  });

  const sendSuccessMessage = (email: string) => {
    notify({
      type: 'success',
      message: (
        <Translate
          i18nKey="authentication.reset_password.success_message"
          values={{ email }}
        />
      ),
    });
  };

  const onSubmit =
    (close: () => void) =>
    async ({ email }: IResetPasswordFormData) => {
      AnalyticsService.trackCustomEvent('recover_password_button_clicked');
      try {
        await requestPasswordReset({ email });
        sendSuccessMessage(email);
        close();
      } catch (err: any) {
        const { statusCode } = err;
        if (statusCode === 422) {
          sendSuccessMessage(email);
        }
      }
    };

  return (
    <Modal
      title={t('authentication.reset_password.title')}
      icon={<Icon icon="key" />}
      iconBadgeColor="accentSecondary20"
      onClose={onClose}
      footer={({ close }) => (
        <Stack horizontal gap="md">
          <Button onPress={close} variant="secondary" loading={isLoading}>
            {t('general.navigation.cancel')}
          </Button>
          <Button
            onPress={methods.handleSubmit(onSubmit(close))}
            className={styles.button}
            loading={isLoading}
          >
            {t('authentication.reset_password.button')}
          </Button>
        </Stack>
      )}
    >
      {({ close }) => (
        <FormProvider {...methods}>
          <Form onSubmit={methods.handleSubmit(onSubmit(close))}>
            <ControlledTextField
              name="email"
              label={t('authentication.reset_password.email.label')}
            />
          </Form>
        </FormProvider>
      )}
    </Modal>
  );
}

const styles = {
  button: 'flex-1',
};
